import type { GenerationRecoveryResult, GenerationWorkflowStatus, SimulationDetail } from '../api'

export interface GenerationRecoveryView {
  status: GenerationWorkflowStatus | null
  busy: 'checking' | 'recovering' | null
  confirmation: boolean
  error: string | null
  notice: string | null
}

export const INITIAL_RECOVERY_VIEW: GenerationRecoveryView = {
  status: null,
  busy: null,
  confirmation: false,
  error: null,
  notice: null,
}

export function canRecoverGeneration(status: GenerationWorkflowStatus | null): boolean {
  return (
    status?.recoverable === true &&
    (status.simulationStatus === 'queued' || status.simulationStatus === 'running') &&
    (status.workflowStatus === 'errored' || status.workflowStatus === 'terminated') &&
    (status.linkage === 'stored' || status.linkage === 'legacy-initial')
  )
}

/** One detail/session lifetime. Neither a failed request nor elapsed time proves a stopped job. */
export function createGenerationRecovery(
  port: {
    refresh: () => Promise<{ status: GenerationWorkflowStatus; simulation: SimulationDetail }>
    recover: () => Promise<GenerationRecoveryResult>
    changed: (simulation: SimulationDetail) => void
  },
  update: (view: GenerationRecoveryView) => void,
  isCurrent: () => boolean,
) {
  let disposed = false
  let locked = false
  let view = { ...INITIAL_RECOVERY_VIEW }
  const current = () => !disposed && isCurrent()
  const publish = (patch: Partial<GenerationRecoveryView>) => {
    if (!current()) return
    view = { ...view, ...patch }
    update(view)
  }
  const refresh = async () => {
    const next = await port.refresh()
    if (!current()) return
    publish({ status: next.status })
    port.changed(next.simulation)
  }
  return {
    async check() {
      if (!current() || locked) return
      locked = true
      publish({ busy: 'checking', status: null, confirmation: false, error: null, notice: null })
      try {
        await refresh()
      } catch {
        publish({ error: '작업 상태를 불러오지 못했어요. 확인 후 다시 조회해 주세요.' })
      } finally {
        locked = false
        publish({ busy: null })
      }
    },
    ask() {
      if (current() && !locked && canRecoverGeneration(view.status))
        publish({ confirmation: true, error: null, notice: null })
    },
    cancel() {
      if (!locked) publish({ confirmation: false })
    },
    async recover() {
      if (!current() || locked || !view.confirmation || !canRecoverGeneration(view.status)) return
      locked = true
      publish({ busy: 'recovering', status: null, confirmation: false, error: null, notice: null })
      try {
        const outcome = await port.recover()
        if (!current()) return
        const notice =
          outcome.status === 'done'
            ? '저장된 완료 결과를 유지했어요. 새 AI 생성은 시작하지 않았어요.'
            : outcome.refunded
              ? '실패 상태로 정리하고 이번 제품권 차감을 반환했어요. 새 AI 생성은 시작하지 않았어요.'
              : '실패 상태로 정리했어요. 새 AI 생성은 시작하지 않았어요.'
        publish({ notice })
        await refresh()
      } catch {
        // A 409, lost response or network failure all require a fresh server observation.
        publish({ error: '복구 결과를 확정하지 못했어요. 최신 작업 상태를 다시 확인해 주세요.' })
        if (current()) {
          try {
            await refresh()
          } catch {
            publish({ status: null })
          }
        }
      } finally {
        locked = false
        publish({ busy: null })
      }
    },
    dispose() {
      disposed = true
    },
  }
}
